// ============================================================
// V2 platform — role + permission resolution.
//
// R6 (2026-04-24) — see ARCHITECTURE.md §X (R6 section will be
// added by principal-architect in a follow-up commit) and
// DESIGN_LOG R6 entry. Owners: principal-architect (shape) +
// auth-security (Rules contract) + firebase-expert (impl) +
// data-architect (subcollection schema).
//
// What this is for
// ────────────────
// Industry packs (src/industries/*.js) declare a `roles` map:
//
//   roles: {
//     director: { permissions: ['*'], systemRole: true, label: 'Director' },
//     'production-supervisor': { permissions: ['inventory.read','inventory.move','production.*'], label: 'Production Supervisor' },
//     ...
//   }
//
// At onboard-tenant OR apply-pack time we MATERIALIZE these into
// per-tenant `tenants/{tid}/roles/{roleId}` documents. Wildcards
// like `inventory.*` are EXPANDED at materialization time using
// the registry's per-module permission lists (so the Firestore-
// stored arrays contain only LITERAL permission strings — Rules
// can't expand wildcards, see DESIGN_LOG R6 sub-decision 3).
//
// The materialized subcollection is the runtime source of truth:
//   - Firestore Rules query it via `hasPerm(perm)` helper.
//   - Settings UI subscribes to it for the Roles & Permissions card.
//   - set-role.js validates against it (was hardcoded VALID_ROLES).
//
// SYSTEM ROLES (director + admin) get a fast-path in Rules — their
// `*` permission is checked via `isDirector() || isAdmin()` direct
// claim read, NOT via a doc lookup. The doc still gets written so
// the UI shows them and a future R6.5 platform-admin can override.
//
// IMPORTANT — wildcard semantics: `*` (literal star) means "all
// permissions, super-user". `<module>.*` is a pack-author wildcard
// EXPANDED to literals at write time using the registry. The Firestore
// docs never store `<module>.*` — only `*` and literals.
// ============================================================

import {
  collection, doc, getDoc, getDocs, setDoc, writeBatch, onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { registry } from './registry.js';

// ─────────────────────────────────────────────────────────────
// SYSTEM ROLES — every tenant always has these. Industry packs
// MAY declare them too (recommended — sets the label per industry),
// but cannot remove them.
// ─────────────────────────────────────────────────────────────

export const SYSTEM_ROLE_IDS = ['director', 'admin'];

/**
 * Default system role definitions. Used as a fallback if an industry
 * pack omits these (it should not — Manufacturing/Trading/Services/Retail
 * all declare them in R6, but defense-in-depth here).
 *
 * Director: super-user, the full `*` permission. Cannot be downgraded.
 * Admin:    all module permissions EXCEPT `tenant.config` (the special
 *           "edit the tenant doc itself" permission — apply pack, change
 *           industry, etc.). Director-only.
 */
export const SYSTEM_ROLE_DEFAULTS = {
  director: {
    id:          'director',
    label:       'Director',
    description: 'Full access including tenant configuration and platform admin.',
    permissions: ['*'],
    systemRole:  true
  },
  admin: {
    id:          'admin',
    label:       'Admin',
    description: 'Full operational access. Cannot edit tenant configuration (industry, modules, branding).',
    // Admin gets "* minus tenant.config" — implemented at materialization
    // time as ALL registered permissions minus the tenant-config bucket.
    permissions: ['__ALL_EXCEPT_TENANT_CONFIG__'],
    systemRole:  true
  }
};

// Permission strings that are RESERVED for the platform itself and
// only directors get them by default. These are NEVER added to the
// admin role's permission list during materialization.
export const TENANT_CONFIG_PERMISSIONS = [
  'tenant.config',           // edit tenants/{tid} doc (industry, modules, theme)
  'tenant.roles.write',      // edit tenants/{tid}/roles/{roleId} (R6.5 override UI)
  'tenant.delete'            // far-future — destroy a tenant (Phase 9+)
];

// ─────────────────────────────────────────────────────────────
// Wildcard expansion — pack-author convenience translated to
// literal permission strings AT WRITE TIME.
// ─────────────────────────────────────────────────────────────

/**
 * Given a list of permission strings (which may contain `<module>.*`
 * wildcards), expand to the literal set.
 *
 *   ['inventory.*', 'sales.read']  →  ['inventory.read', 'inventory.write',
 *                                       'inventory.move', 'inventory.delete',
 *                                       'sales.read']
 *
 * Resolution rules:
 *   - `*`             — kept as-is (super-user marker; system roles only).
 *   - `<module>.*`    — expanded to all permissions declared by the module
 *                       with id `<module>` in the registry. If the module
 *                       isn't registered, the wildcard is DROPPED with a
 *                       console.warn (don't silently grant nothing).
 *   - `!<perm>`       — negation; removes the permission AFTER expansion.
 *                       Used by admin role's `!tenant.config` pattern.
 *   - literal         — kept as-is.
 *
 * Returns a de-duplicated, sorted array.
 */
export function expandWildcards(permList, registryRef = registry) {
  if (!Array.isArray(permList)) return [];
  const positive = new Set();
  const negative = new Set();

  for (const raw of permList) {
    if (!raw || typeof raw !== 'string') continue;
    const p = raw.trim();
    if (!p) continue;

    // Negation
    if (p.startsWith('!')) {
      negative.add(p.slice(1));
      continue;
    }

    // Super-user
    if (p === '*') {
      positive.add('*');
      continue;
    }

    // Special internal marker — admin's "all except tenant config" placeholder.
    if (p === '__ALL_EXCEPT_TENANT_CONFIG__') {
      // Walk every registered manifest's permissions
      for (const m of registryRef.getAllManifests()) {
        if (!Array.isArray(m.permissions)) continue;
        for (const perm of m.permissions) {
          if (!TENANT_CONFIG_PERMISSIONS.includes(perm)) positive.add(perm);
        }
      }
      continue;
    }

    // Module wildcard
    if (p.endsWith('.*')) {
      const moduleId = p.slice(0, -2);
      const mod = registryRef.getManifest(moduleId);
      if (!mod || !Array.isArray(mod.permissions)) {
        console.warn('[permissions] Wildcard "' + p + '" — module "' + moduleId + '" not registered. Dropping.');
        continue;
      }
      for (const perm of mod.permissions) positive.add(perm);
      continue;
    }

    // Literal
    positive.add(p);
  }

  // Apply negations
  for (const n of negative) positive.delete(n);

  return Array.from(positive).sort();
}

// ─────────────────────────────────────────────────────────────
// Materialization — write pack roles into tenants/{tid}/roles/.
// ─────────────────────────────────────────────────────────────

/**
 * Write the pack's role definitions into `tenants/{tid}/roles/{roleId}`.
 *
 * Behavior:
 *   - INSERT-IF-MISSING by default. If a role doc already exists,
 *     it is NOT overwritten (preserves admin-customized permissions).
 *   - When `opts.overwriteCustomized: true` is passed, ALL pack roles
 *     are written unconditionally (used by "Reset to pack defaults"
 *     in the future R6.5 UI).
 *   - System roles (director, admin) are ALWAYS written if missing,
 *     even if the pack omits them. This guarantees every tenant has
 *     director + admin available.
 *   - Wildcards expanded at write time. Stored permissions are literals
 *     plus the special `*` (super-user, fast-path in Rules).
 *
 * @param {string} tenantId
 * @param {object} pack            resolved industry pack object (with .roles map)
 * @param {object} [opts]
 * @param {boolean} [opts.overwriteCustomized=false]
 * @param {object} [opts.user]     { email } for audit stamps
 * @returns {Promise<{ written: string[], skipped: string[] }>}
 */
export async function materializeRolesForPack(tenantId, pack, opts = {}) {
  if (!tenantId)   throw new Error('materializeRolesForPack: tenantId required');
  if (!pack)       throw new Error('materializeRolesForPack: pack required');
  const overwrite = !!opts.overwriteCustomized;
  const userEmail = (opts.user && opts.user.email) || 'platform';

  // Build the effective role map: pack.roles ∪ SYSTEM_ROLE_DEFAULTS.
  // Pack-declared roles take precedence over the system defaults (a pack
  // can re-label "Director" → "Founder" for a particular industry).
  const effective = {};
  for (const id of SYSTEM_ROLE_IDS) {
    effective[id] = SYSTEM_ROLE_DEFAULTS[id];
  }
  if (pack.roles && typeof pack.roles === 'object') {
    for (const [id, def] of Object.entries(pack.roles)) {
      if (!id || !def) continue;
      effective[id] = { id, ...def };
    }
  }

  // Read existing roles for skip-if-present.
  const existing = await loadRolesForTenant(tenantId);
  const existingIds = new Set(Object.keys(existing));

  const written = [];
  const skipped = [];
  const batch = writeBatch(db);

  for (const [roleId, def] of Object.entries(effective)) {
    if (existingIds.has(roleId) && !overwrite) {
      skipped.push(roleId);
      continue;
    }

    const expandedPerms = expandWildcards(def.permissions || []);

    const docData = {
      id:          roleId,
      label:       def.label || roleId,
      description: def.description || '',
      permissions: expandedPerms,
      systemRole:  !!def.systemRole,
      packDefault: true,                // unaltered pack-shipped role
      packId:      pack.id || '',
      CreatedAt:   serverTimestamp(),
      UpdatedAt:   serverTimestamp(),
      CreatedBy:   userEmail,
      UpdatedBy:   userEmail
    };

    batch.set(doc(db, 'tenants', tenantId, 'roles', roleId), docData);
    written.push(roleId);
  }

  if (written.length > 0) await batch.commit();

  return { written, skipped };
}

// ─────────────────────────────────────────────────────────────
// Read-side helpers — used by UI + set-role.js validation.
// ─────────────────────────────────────────────────────────────

/**
 * One-shot read of all roles for a tenant. Returns a map keyed by roleId.
 * Empty map if the subcollection doesn't exist yet (pre-R6 materialization).
 *
 * @returns {Promise<Object<string, RoleDoc>>}
 */
export async function loadRolesForTenant(tenantId) {
  if (!tenantId) throw new Error('loadRolesForTenant: tenantId required');
  const snap = await getDocs(collection(db, 'tenants', tenantId, 'roles'));
  const out = {};
  snap.forEach(d => { out[d.id] = { id: d.id, ...d.data() }; });
  return out;
}

/**
 * Subscribe to live role changes for a tenant. Used by Settings UI
 * Roles & Permissions card. Returns an unsubscribe function.
 *
 * @param {string}   tenantId
 * @param {(rolesMap: Object<string, RoleDoc>) => void} onChange
 */
export function subscribeRolesForTenant(tenantId, onChange) {
  if (!tenantId) throw new Error('subscribeRolesForTenant: tenantId required');
  return onSnapshot(
    collection(db, 'tenants', tenantId, 'roles'),
    (snap) => {
      const out = {};
      snap.forEach(d => { out[d.id] = { id: d.id, ...d.data() }; });
      onChange(out);
    }
  );
}

/**
 * Read a single role doc.
 *
 * @returns {Promise<RoleDoc|null>}
 */
export async function loadRoleForTenant(tenantId, roleId) {
  if (!tenantId || !roleId) return null;
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'roles', roleId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Permission check helper — symmetric to Firestore Rules' hasPerm().
 * Used by client-side UI for "should I show this button?" cooperative
 * gating. Server-side enforcement still lives in firestore.rules.
 *
 * Fast-path:
 *   - role === 'director'  → true (super-user)
 *   - permissions includes '*' → true
 *   - permissions includes the literal → true
 *
 * @param {string} role          the user's current role string
 * @param {string} permission    the permission to check (e.g. 'inventory.write')
 * @param {Object<string, RoleDoc>} rolesMap  output of loadRolesForTenant
 * @returns {boolean}
 */
export function hasPermission(role, permission, rolesMap) {
  if (!role || !permission) return false;
  // System fast-path — director is always super-user.
  if (role === 'director') return true;
  // 2026-08-15 roadmap — client mirrored Rules' isAdmin fast-path
  // (`isAdmin() && !perm.matches('^tenant[.].*')`); without it admin is
  // denied any perm added after the last materialization (the stored
  // admin role doc only lists the permissions that existed when
  // r6-materialize-roles / apply-pack last ran, so a brand-new module's
  // `<module>.read` is missing until re-materialization — but Rules
  // already let admin through). Same shape as Rules: everything except
  // the tenant.* family.
  if (role === 'admin' && !/^tenant\./.test(permission)) return true;
  if (!rolesMap) return false;
  const def = rolesMap[role];
  if (!def || !Array.isArray(def.permissions)) return false;
  if (def.permissions.includes('*')) return true;
  return def.permissions.includes(permission);
}

/**
 * @typedef RoleDoc
 * @prop {string}   id
 * @prop {string}   label
 * @prop {string}   description
 * @prop {string[]} permissions   literal permission strings + optional '*'
 * @prop {boolean}  systemRole
 * @prop {boolean}  packDefault   true if untouched since materialization
 * @prop {string}   packId        the industry pack id this role came from
 */
